const ALLOWED_TAGS = new Set([
  'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'a', 'ul', 'ol', 'li',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'span', 'div', 'hr',
  'sub', 'sup', 'code', 'pre',
]);

const SAFE_HREF_PREFIX = /^https?:\/\//i;

/**
 * Sanitizes HTML for safe display (e.g. dangerouslySetInnerHTML).
 * Uses DOMParser + allowlist; no scripts/events/javascript: URLs.
 * Links get target="_blank" and rel="noopener noreferrer".
 */
export function sanitizeHtml(html: string): string {
  if (!html || typeof html !== 'string') return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const body = doc.body;

  function process(node: Node): Node[] {
    if (node.nodeType === Node.TEXT_NODE) return [node.cloneNode(true)];
    if (node.nodeType !== Node.ELEMENT_NODE) return [];
    const el = node as Element;
    const tag = el.tagName.toLowerCase();
    if (!ALLOWED_TAGS.has(tag)) {
      return Array.from(el.childNodes).flatMap(process);
    }
    const safe = doc.createElement(tag);
    if (tag === 'a') {
      const href = (el.getAttribute('href') ?? '').trim();
      if (SAFE_HREF_PREFIX.test(href)) safe.setAttribute('href', href);
      safe.setAttribute('target', '_blank');
      safe.setAttribute('rel', 'noopener noreferrer');
    }
    Array.from(el.childNodes).forEach((c) => {
      process(c).forEach((n) => safe.appendChild(n));
    });
    return [safe];
  }

  const fragment = doc.createDocumentFragment();
  Array.from(body.childNodes).forEach((c) => {
    process(c).forEach((n) => fragment.appendChild(n));
  });
  body.innerHTML = '';
  body.appendChild(fragment);
  return body.innerHTML;
}

/**
 * Strips HTML tags and returns plain text.
 * Uses DOMParser for safe parsing (does not execute scripts).
 */
export function stripHtml(html: string): string {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}

/**
 * Checks if an HTML string has meaningful text content.
 * Returns false for empty strings or HTML with only empty tags/whitespace.
 */
/**
 * Detects whether a string contains HTML markup.
 */
function isHtmlContent(text: string): boolean {
  if (!text) return false;
  return /<[a-z][a-z0-9]*[\s>\/]/i.test(text);
}

/**
 * Prepares description content for HTML rendering.
 * Plain text (no HTML tags): escapes entities and converts newlines to <br>.
 * HTML content: returns as-is.
 */
export function prepareHtmlContent(text: string): string {
  if (!text) return '';
  // Clean up escaped quotes from backend double-encoding
  let cleaned = text.replace(/\\"/g, '"').replace(/\\'/g, "'");
  if (isHtmlContent(cleaned)) return cleaned;
  return cleaned
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br>');
}

export function hasHtmlContent(html: string): boolean {
  if (!html) return false;
  const text = stripHtml(html).trim();
  return text.length > 0;
}
